/**
 * /tools/eulogy?clientId=X — STP-024 + F-030 (2026-05-24).
 *
 * Step 1 reflection tool — REWRITE for the axis-based design. The client
 * walks three focused pages (Energy → Work → Love), writing a eulogy
 * paragraph and naming 0-5 virtues per axis. Optional final reflection
 * after Love.
 *
 * Output feeds the Carpe Diem Pool with high-confidence per-axis virtue
 * proposals via the eulogy mapper. Coach can AI-extract additional
 * virtues from the paragraphs (macOS-only surface).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type Axis = 'energy' | 'work' | 'love';
const AXES: Axis[] = ['energy', 'work', 'love'];

interface AxisBlock {
  text: string;
  virtues: string[];
}

interface FormState {
  energy: AxisBlock;
  work: AxisBlock;
  love: AxisBlock;
  reflection: string;
}

const DEFAULT_BLOCK: AxisBlock = { text: '', virtues: [] };
const DEFAULT_FORM: FormState = {
  energy: { ...DEFAULT_BLOCK },
  work: { ...DEFAULT_BLOCK },
  love: { ...DEFAULT_BLOCK },
  reflection: '',
};

export function EulogyPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [page, setPage] = useState<0 | 1 | 2>(0); // 0=energy, 1=work, 2=love
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // F-030 follow-up #2 (2026-05-24) — surface hydration failures inline
  // instead of swallowing them. Coach reported the form opened empty even
  // though completed data existed; without a visible error we couldn't tell
  // whether the GET 404'd, the response shape mismatched, or the parse
  // threw. Now any non-null value renders a banner above the form.
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  // F-030 follow-up — disabled-button-as-saved-indicator (F-011 pattern).
  // The Submit button is disabled when the form matches the last saved /
  // hydrated baseline; becomes enabled the moment any axis page is edited.
  const { isPristine, markSaved } = usePristine<FormState>(DEFAULT_FORM);

  // Hydrate from existing completion (if any) so a re-open shows the
  // client's prior content + lets them refine it.
  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'eulogy');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = {
            energy: { ...DEFAULT_BLOCK },
            work: { ...DEFAULT_BLOCK },
            love: { ...DEFAULT_BLOCK },
            reflection: '',
          };
          for (const axis of AXES) {
            const block = p[axis] as Record<string, unknown> | undefined;
            if (block) {
              next[axis] = {
                text: typeof block.text === 'string' ? block.text : '',
                virtues: Array.isArray(block.virtues)
                  ? block.virtues.filter((v): v is string => typeof v === 'string').slice(0, 5)
                  : [],
              };
            }
          }
          if (typeof p.reflection === 'string') next.reflection = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
          // Baseline = hydrated values so the button starts disabled.
          markSaved(next);
        } else {
          // No prior completion → baseline = empty defaults so the button
          // enables as soon as the client types anything.
          markSaved(DEFAULT_FORM);
        }
      } catch (cause) {
        if (!active) return;
        // Surface the cause so an empty form is never silent.
        // 401/403 → coach/client isn't logged in or lacks access; ApiError
        // carries the server's `message`. Network/parse errors fall through
        // to the generic banner.
        // eslint-disable-next-line no-console
        console.error('[eulogy] hydration failed', cause);
        if (cause instanceof ApiError) {
          setHydrationError(
            cause.envelope.message || t('tools.eulogy.errors.hydrationFailed'),
          );
        } else {
          setHydrationError(t('tools.eulogy.errors.hydrationFailed'));
        }
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, t]);

  const currentAxis = AXES[page]!;
  const isLastPage = page === 2;

  const canSubmit =
    !submitting &&
    hydrated &&
    form.energy.text.trim().length > 0 &&
    form.work.text.trim().length > 0 &&
    form.love.text.trim().length > 0 &&
    !isPristine(form);

  const canAdvance = form[currentAxis].text.trim().length > 0;

  const updateBlock = (axis: Axis, partial: Partial<AxisBlock>): void => {
    setForm((prev) => ({ ...prev, [axis]: { ...prev[axis], ...partial } }));
  };

  const setText = (axis: Axis, text: string): void => updateBlock(axis, { text });
  const addVirtue = (axis: Axis, raw: string): void => {
    const trimmed = raw.replace(/\s+/g, ' ').trim().slice(0, 60);
    if (trimmed.length === 0) return;
    const current = form[axis].virtues;
    if (current.length >= 5) return;
    if (current.some((v) => v.toLowerCase() === trimmed.toLowerCase())) return;
    updateBlock(axis, { virtues: [...current, trimmed] });
  };
  const removeVirtue = (axis: Axis, idx: number): void => {
    updateBlock(axis, { virtues: form[axis].virtues.filter((_, i) => i !== idx) });
  };

  const onSubmit = async (): Promise<void> => {
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.eulogy.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      energy: { text: form.energy.text.trim(), virtues: form.energy.virtues },
      work: { text: form.work.text.trim(), virtues: form.work.virtues },
      love: { text: form.love.text.trim(), virtues: form.love.virtues },
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'eulogy',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      // F-030 follow-up — baseline now = just-saved form. Button flips
      // back to disabled until the client edits again.
      markSaved(form);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.eulogy.errors.generic'));
      } else {
        setSubmitError(t('tools.eulogy.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="eulogy-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.eulogy.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.eulogy.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.eulogy.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.eulogy.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.eulogy.description')}</p>
      </header>

      {hydrationError && (
        <p
          role="alert"
          className="form-error mb-s4"
          data-testid="eulogy-hydration-error"
        >
          {hydrationError}
        </p>
      )}

      <ProgressBar page={page} />

      <AxisPage
        axis={currentAxis}
        block={form[currentAxis]}
        onTextChange={(text) => setText(currentAxis, text)}
        onAddVirtue={(v) => addVirtue(currentAxis, v)}
        onRemoveVirtue={(idx) => removeVirtue(currentAxis, idx)}
        t={t}
      />

      {isLastPage && (
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5 mt-s4">
          <label className="block">
            <span className="form-label">
              {t('tools.eulogy.reflection.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.eulogy.optional')}
              </span>
            </span>
            <p className="text-xs text-gray-1 mb-s2">{t('tools.eulogy.reflection.hint')}</p>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              rows={3}
              data-testid="eulogy-reflection"
            />
          </label>
        </article>
      )}

      {submitError && (
        <p role="alert" className="form-error mt-s4" data-testid="eulogy-submit-error">
          {submitError}
        </p>
      )}

      {savedAt && (
        <p
          role="status"
          className="text-sm text-flame-deep mt-s4"
          data-testid="eulogy-saved-at"
        >
          ✓ {t('tools.eulogy.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
        </p>
      )}

      <div className="flex items-center justify-between gap-s3 mt-s6">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setPage((p) => (p > 0 ? ((p - 1) as 0 | 1 | 2) : p))}
          disabled={page === 0 || submitting}
          data-testid="eulogy-back"
        >
          {t('tools.eulogy.nav.back')}
        </button>
        {isLastPage ? (
          <>
            <FinalizeWarning />
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                void onSubmit();
              }}
              disabled={!canSubmit}
              data-testid="eulogy-submit"
            >
              {submitting ? t('tools.eulogy.submitting') : t('tools.eulogy.submit')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setPage((p) => Math.min(p + 1, 2) as 0 | 1 | 2)}
            disabled={!canAdvance}
            data-testid="eulogy-next"
          >
            {t('tools.eulogy.nav.next')}
          </button>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────  Sub-components  ───────────────────────────── */

function ProgressBar({ page }: { page: 0 | 1 | 2 }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-s4" data-testid="eulogy-progress">
      {AXES.map((axis, idx) => {
        const isActive = idx === page;
        const isDone = idx < page;
        return (
          <div key={axis} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                  isDone
                    ? 'bg-flame-deep text-bone-1'
                    : isActive
                      ? 'bg-flame-glow text-flame-deep ring-2 ring-flame-deep'
                      : 'bg-bone-2 text-gray-2'
                }`}
              >
                {idx + 1}
              </div>
              <span
                className={`text-xs mt-s1 ${
                  isActive ? 'text-flame-deep font-semibold' : 'text-gray-1'
                }`}
              >
                {t(`tools.eulogy.axis.${axis}.label`)}
              </span>
            </div>
            {idx < 2 && (
              <div
                className={`h-px flex-1 -mt-s4 ${isDone ? 'bg-flame-deep' : 'bg-bone-3'}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface AxisPageProps {
  axis: Axis;
  block: AxisBlock;
  onTextChange: (text: string) => void;
  onAddVirtue: (v: string) => void;
  onRemoveVirtue: (idx: number) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function AxisPage({
  axis,
  block,
  onTextChange,
  onAddVirtue,
  onRemoveVirtue,
  t,
}: AxisPageProps): JSX.Element {
  const [draft, setDraft] = useState('');
  return (
    <div className="space-y-s4">
      <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
        <p className="font-display text-display-md text-ink">
          {t(`tools.eulogy.axis.${axis}.title`)}
        </p>
        <p className="mt-s2 text-sm italic text-gray-2 max-w-prose">
          {t(`tools.eulogy.axis.${axis}.prompt`)}
        </p>
        <textarea
          value={block.text}
          onChange={(e) => onTextChange(e.target.value)}
          className="input-field mt-s3 min-h-40"
          rows={8}
          placeholder={t(`tools.eulogy.axis.${axis}.placeholder`)}
          data-testid={`eulogy-text-${axis}`}
        />
      </article>

      <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
        <p className="form-label">
          {t('tools.eulogy.virtues.label')}
          <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
            {t('tools.eulogy.virtues.cap', { current: block.virtues.length, max: 5 })}
          </span>
        </p>
        <p className="text-xs text-gray-1 mb-s3">{t('tools.eulogy.virtues.hint')}</p>

        {block.virtues.length > 0 && (
          <ul
            className="flex flex-wrap gap-s2 mb-s3"
            data-testid={`eulogy-virtues-${axis}`}
          >
            {block.virtues.map((v, idx) => (
              <li
                key={`${v}-${idx}`}
                className="inline-flex items-center gap-s1 rounded-full bg-flame-glow border border-flame-deep px-s3 py-s1 text-sm text-flame-deep"
              >
                <span>{v}</span>
                <button
                  type="button"
                  onClick={() => onRemoveVirtue(idx)}
                  className="text-flame-deep/70 hover:text-flame-deep"
                  aria-label={t('tools.eulogy.virtues.remove', { virtue: v })}
                  data-testid={`eulogy-virtue-remove-${axis}-${idx}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {block.virtues.length < 5 && (
          <div className="flex gap-s2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  onAddVirtue(draft);
                  setDraft('');
                }
              }}
              maxLength={60}
              placeholder={t('tools.eulogy.virtues.placeholder')}
              className="input-field flex-1"
              data-testid={`eulogy-virtue-input-${axis}`}
            />
            <button
              type="button"
              onClick={() => {
                onAddVirtue(draft);
                setDraft('');
              }}
              disabled={draft.trim().length === 0}
              className="btn-secondary"
              data-testid={`eulogy-virtue-add-${axis}`}
            >
              {t('tools.eulogy.virtues.add')}
            </button>
          </div>
        )}
      </article>
    </div>
  );
}
